import React, { useMemo, useState } from 'react';
import moment from 'moment/moment';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import GenericTableSkeleton from '../../../components/agv-generic-table/genericTableSkeleton';
import useGetTourLogsIndicators from '../../../api/tours/useGetTourLogsIndicators';
import { formatDate } from '../logs.utils';

const PERIODS = [7, 30, 90];

// Couleur principale de l'application pour les séries uniques ; couleurs d'état pour les envois
// non transmis, dans un ordre validé (daltonisme) : rouge et orange ne sont pas côte à côte
const MAIN_COLOR = '#079992';
const NOT_SENT_SERIES = [
  { key: 'INTERVENTION_REQUIRED', label: 'Action requise', color: '#FC5959' },
  { key: 'PENDING', label: 'En attente d’envoi', color: '#17A2B8' },
  { key: 'AUTO_RETRY', label: 'Nouvel essai prévu', color: '#FF8E0A' },
];

const AXIS_PROPS = {
  tick: { fontSize: 11, fill: '#6B7280' },
  axisLine: false,
  tickLine: false,
};

function formatMinutes(minutes) {
  if (minutes === null || minutes === undefined) {
    return '—';
  }
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${String(rest).padStart(2, '0')}` : `${hours} h`;
}

function StatTile({ label, value, detail }) {
  return (
    <div className="tw-flex tw-flex-col tw-gap-1 tw-p-4 tw-rounded-md tw-bg-white tw-border tw-border-solid tw-border-black/10">
      <span className="tw-text-xs tw-font-semibold tw-font-work tw-text-text-dark-medium-importance tw-uppercase tw-tracking-wide">
        {label}
      </span>
      <span className="tw-text-2xl tw-font-semibold tw-leading-7">{value}</span>
      {detail && (
        <span className="tw-text-xs tw-font-work tw-text-text-dark-medium-importance">
          {detail}
        </span>
      )}
    </div>
  );
}

function ChartCard({ title, description, children }) {
  return (
    <section className="tw-flex tw-flex-col tw-gap-2 tw-p-4 tw-rounded-md tw-bg-white tw-border tw-border-solid tw-border-black/10">
      <div>
        <p
          role="heading"
          aria-level={3}
          className="tw-m-0 tw-text-sm tw-font-semibold"
        >
          {title}
        </p>
        {description && (
          <p className="tw-m-0 tw-text-xs tw-text-text-dark-medium-importance">
            {description}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

function ChartTooltip({ active, payload, label, labelFormatter }) {
  if (!active || !payload?.length) {
    return null;
  }
  return (
    <div className="tw-p-2 tw-rounded tw-bg-white tw-border tw-border-solid tw-border-black/10 tw-shadow-sm tw-text-xs">
      <div className="tw-font-semibold tw-mb-1">
        {labelFormatter ? labelFormatter(label) : label}
      </div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="tw-flex tw-items-center tw-gap-2">
          <span
            className="tw-inline-block tw-size-2 tw-rounded-full"
            style={{ backgroundColor: entry.color }}
            aria-hidden="true"
          />
          <span>{entry.name} :</span>
          <span className="tw-font-semibold">
            {Number(entry.value).toLocaleString('fr-FR')}
          </span>
        </div>
      ))}
    </div>
  );
}

// « 174 nouveaux envois demandés · 48 corrections », « 1 nouvel envoi demandé · 0 correction »
function formatManualActions(manualActions) {
  const retries = manualActions?.retries || 0;
  const edits = manualActions?.edits || 0;
  const retriesText =
    retries > 1
      ? `${retries.toLocaleString('fr-FR')} nouveaux envois demandés`
      : `${retries} nouvel envoi demandé`;
  const editsText = `${edits.toLocaleString('fr-FR')} correction${edits > 1 ? 's' : ''}`;
  return `${retriesText} · ${editsText}`;
}

/**
 * Onglet « Indicateurs » : qualité de la liaison avec l'AS400 Cooperl sur une période
 * (date de tournée).
 */
export default function LogsIndicators() {
  const [periodDays, setPeriodDays] = useState(30);
  const period = useMemo(
    () => ({
      start_date: moment()
        .subtract(periodDays - 1, 'days')
        .format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
    }),
    [periodDays]
  );
  const { indicators, error, isLoading } = useGetTourLogsIndicators(period);

  const daily = useMemo(
    () =>
      (indicators?.daily || []).map((day) => ({
        ...day,
        label: moment(day.day).format('DD/MM'),
        total:
          day.DONE + day.AUTO_RETRY + day.INTERVENTION_REQUIRED + day.PENDING,
      })),
    [indicators]
  );
  const maxRefusals = Math.max(
    1,
    ...(indicators?.topRefusals || []).map((refusal) => refusal.count)
  );

  const firstAttempt = indicators?.firstAttempt;
  const delay = indicators?.transmissionDelay;
  const totals = indicators?.totals;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <div
          role="group"
          aria-label="Période"
          className="tw-inline-flex tw-rounded-md tw-border tw-border-solid tw-border-black/10 tw-bg-white tw-overflow-hidden"
        >
          {PERIODS.map((days) => (
            <button
              key={days}
              type="button"
              aria-pressed={periodDays === days}
              onClick={() => setPeriodDays(days)}
              className={`tw-px-3 tw-py-1.5 tw-text-sm tw-border-0 tw-cursor-pointer ${
                periodDays === days
                  ? 'tw-bg-main tw-text-white'
                  : 'tw-bg-white hover:tw-bg-[#F7F8FA]'
              }`}
            >
              {days} jours
            </button>
          ))}
        </div>
        <span className="tw-text-xs tw-text-text-dark-medium-importance">
          Tournées du {formatDate(period.start_date)} au{' '}
          {formatDate(period.end_date)}
        </span>
      </div>

      {error && (
        <p className="tw-m-0 tw-text-sm tw-text-status-negative" role="alert">
          {error}
        </p>
      )}

      {isLoading && !indicators ? (
        <GenericTableSkeleton />
      ) : (
        indicators && (
          <div
            className={`tw-flex tw-flex-col tw-gap-4 tw-transition-opacity ${
              isLoading ? 'tw-opacity-50' : ''
            }`}
            aria-busy={isLoading}
          >
            <div className="tw-grid tw-grid-cols-4 mq1050:tw-grid-cols-2 tw-gap-3">
              <StatTile
                label="Transmis au premier essai"
                value={
                  firstAttempt?.rate === null ||
                  firstAttempt?.rate === undefined
                    ? '—'
                    : `${Math.round(firstAttempt.rate * 100)} %`
                }
                detail={
                  firstAttempt?.attemptedLogs
                    ? `${firstAttempt.successes.toLocaleString('fr-FR')} sur ${firstAttempt.attemptedLogs.toLocaleString('fr-FR')} envois suivis`
                    : 'Pas encore de donnée sur la période'
                }
              />
              <StatTile
                label="Délai de transmission"
                value={formatMinutes(delay?.medianMinutes)}
                detail={
                  delay?.count
                    ? `Médian · 90 % en moins de ${formatMinutes(delay.p90Minutes)}`
                    : 'Aucun envoi transmis sur la période'
                }
              />
              <StatTile
                label="Envois sur la période"
                value={(totals?.ALL || 0).toLocaleString('fr-FR')}
                detail={`${(totals?.INTERVENTION_REQUIRED || 0).toLocaleString('fr-FR')} en action requise`}
              />
              <StatTile
                label="Interventions manuelles"
                value={(
                  (indicators.manualActions?.retries || 0) +
                  (indicators.manualActions?.edits || 0)
                ).toLocaleString('fr-FR')}
                detail={formatManualActions(indicators.manualActions)}
              />
            </div>

            <div className="tw-grid tw-grid-cols-2 mq1050:tw-grid-cols-1 tw-gap-3">
              <ChartCard
                title="Envois par jour de tournée"
                description="Tous états confondus"
              >
                <div className="tw-h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={daily} barCategoryGap={2}>
                      <CartesianGrid vertical={false} stroke="#EEF0F2" />
                      <XAxis dataKey="label" {...AXIS_PROPS} minTickGap={12} />
                      <YAxis {...AXIS_PROPS} allowDecimals={false} width={40} />
                      <Tooltip
                        cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                        content={<ChartTooltip />}
                      />
                      <Bar
                        dataKey="total"
                        name="Envois"
                        fill={MAIN_COLOR}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={18}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>

              <ChartCard
                title="Envois non transmis par jour de tournée"
                description="Selon leur état actuel"
              >
                <div className="tw-h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={daily} barCategoryGap={2}>
                      <CartesianGrid vertical={false} stroke="#EEF0F2" />
                      <XAxis dataKey="label" {...AXIS_PROPS} minTickGap={12} />
                      <YAxis {...AXIS_PROPS} allowDecimals={false} width={40} />
                      <Tooltip
                        cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                        content={<ChartTooltip />}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ fontSize: 12 }}
                      />
                      {NOT_SENT_SERIES.map((series, index) => (
                        <Bar
                          key={series.key}
                          dataKey={series.key}
                          name={series.label}
                          stackId="not-sent"
                          fill={series.color}
                          stroke="#FFFFFF"
                          strokeWidth={1}
                          maxBarSize={18}
                          radius={
                            index === NOT_SENT_SERIES.length - 1
                              ? [4, 4, 0, 0]
                              : [0, 0, 0, 0]
                          }
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>

              <ChartCard
                title="Indisponibilités de l’AS400 par heure"
                description="Échecs sans réponse exploitable de l’AS400 (heure de Paris)"
              >
                <div className="tw-h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={indicators.unavailabilityByHour}
                      barCategoryGap={2}
                    >
                      <CartesianGrid vertical={false} stroke="#EEF0F2" />
                      <XAxis
                        dataKey="hour"
                        {...AXIS_PROPS}
                        tickFormatter={(hour) => `${hour} h`}
                        interval={2}
                      />
                      <YAxis {...AXIS_PROPS} allowDecimals={false} width={40} />
                      <Tooltip
                        cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                        content={
                          <ChartTooltip
                            labelFormatter={(hour) =>
                              `${hour} h – ${hour + 1} h`
                            }
                          />
                        }
                      />
                      <Bar
                        dataKey="count"
                        name="Échecs"
                        fill={MAIN_COLOR}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={18}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>

              <ChartCard
                title="Motifs de refus les plus fréquents"
                description="Données refusées par l’AS400 (nombre d’essais refusés)"
              >
                {indicators.topRefusals?.length ? (
                  <ol className="tw-list-none tw-m-0 tw-p-0 tw-flex tw-flex-col tw-gap-2">
                    {indicators.topRefusals.map((refusal) => (
                      <li
                        key={refusal.message}
                        className="tw-flex tw-flex-col tw-gap-0.5"
                      >
                        <div className="tw-flex tw-justify-between tw-gap-3 tw-text-xs">
                          <span className="tw-break-words">
                            {refusal.message}
                          </span>
                          <span className="tw-shrink-0 tw-font-semibold">
                            {refusal.count.toLocaleString('fr-FR')}
                            <span className="tw-font-normal tw-text-text-dark-medium-importance">
                              {' '}
                              · {refusal.logs} envoi
                              {refusal.logs > 1 ? 's' : ''}
                            </span>
                          </span>
                        </div>
                        <div className="tw-h-1.5 tw-rounded-full tw-bg-[#EEF0F2]">
                          <div
                            className="tw-h-full tw-rounded-full"
                            style={{
                              width: `${(refusal.count / maxRefusals) * 100}%`,
                              backgroundColor: MAIN_COLOR,
                            }}
                          />
                        </div>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="tw-m-0 tw-text-sm tw-text-text-dark-medium-importance">
                    Aucun refus sur la période.
                  </p>
                )}
              </ChartCard>
            </div>

            <p className="tw-m-0 tw-text-xs tw-text-text-dark-medium-importance">
              {indicators.historySince
                ? `Premier essai, refus, indisponibilités et interventions : suivis depuis le ${formatDate(indicators.historySince)}.`
                : 'Premier essai, refus, indisponibilités et interventions : pas encore de donnée suivie sur la période.'}
            </p>
          </div>
        )
      )}
    </div>
  );
}
